import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  Conversation,
  Message,
  PaginationMetadata,
} from "../interfaces/chat.interface";
import { normalizeMessage } from "../../../utils/chat.utils";
import {
  fetchConversations,
  fetchConversationById,
  fetchConversationsByProduct,
  createConversation,
  sendMessage,
  markMessageRead,
  markConversationRead,
  fetchMessages,
} from "../thunks/chat.thunk";

/** Redux slice state for the chat feature. */
export interface ChatState {
  conversations: Conversation[];
  conversationsPagination: PaginationMetadata;
  currentConversation: Conversation | null;
  messages: Message[];
  productConversations: Conversation[];
  productConversationsPagination: PaginationMetadata;
  isLoading: boolean;
  isLoadingMoreConversations: boolean;
  isLoadingProductConversations: boolean;
  isLoadingMoreProductConversations: boolean;
  isSendingMessage: boolean;
  error: any | null;
}

const initialState: ChatState = {
  conversations: [],
  conversationsPagination: {
    total: 0,
    page: 1,
    limit: 15,
    totalPages: 1,
  },
  currentConversation: null,
  messages: [],
  productConversations: [],
  productConversationsPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },
  isLoading: false,
  isLoadingMoreConversations: false,
  isLoadingProductConversations: false,
  isLoadingMoreProductConversations: false,
  isSendingMessage: false,
  error: null,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentConversation: (
      state,
      action: PayloadAction<Conversation | null>,
    ) => {
      state.currentConversation = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    clearProductConversations: (state) => {
      state.productConversations = [];
    },
    // Optimistic message update for real-time functionality
    addMessageOptimistically: (state, action: PayloadAction<Message>) => {
      const incoming = action.payload as any;
      const incomingId = String(incoming.id || "");
      const incomingTempId = String(incoming.tempId || "");

      if (incomingId && !incomingId.startsWith("temp_")) {
        const tempIdx = state.messages.findIndex(
          (m: any) =>
            (m.tempId && String(m.tempId) === incomingTempId) ||
            (m.tempId && String(m.tempId) === incomingId) ||
            (m.id && String(m.id) === incomingId),
        );
        if (tempIdx !== -1) {
          state.messages[tempIdx] = { ...state.messages[tempIdx], ...incoming };
          return;
        }
      }

      if (incomingTempId) {
        const existingIdx = state.messages.findIndex(
          (m: any) =>
            String(m.tempId || "") === incomingTempId ||
            (incomingId && String(m.id || "") === incomingId),
        );
        if (existingIdx !== -1) {
          state.messages[existingIdx] = {
            ...state.messages[existingIdx],
            ...incoming,
          };
          return;
        }
      } else if (incomingId) {
        const existingIdx = state.messages.findIndex(
          (m: any) => String(m.id || "") === incomingId,
        );
        if (existingIdx !== -1) {
          state.messages[existingIdx] = {
            ...state.messages[existingIdx],
            ...incoming,
          };
          return;
        }
      }

      state.messages.push(incoming);
    },
    // Update message when socket confirms delivery or status changes
    updateMessageStatus: (
      state,
      action: PayloadAction<{
        id?: string | number;
        tempId?: string;
        updates: Partial<Message> | any;
      }>,
    ) => {
      const { id, tempId, updates } = action.payload;
      const index = state.messages.findIndex(
        (m) =>
          (id && String(m.id) === String(id)) ||
          (tempId && String((m as any).tempId) === String(tempId)) ||
          (tempId && String(m.id) === String(tempId)),
      );
      if (index !== -1) {
        // Create updated message with normalized status
        const updatedMessage = {
          ...state.messages[index],
          ...updates,
        };

        // Normalize the message status to ensure consistency
        const normalizedMessage = normalizeMessage(updatedMessage);
        state.messages[index] = normalizedMessage;

        // Synchronize with conversation last message if it matches
        const conversationId = normalizedMessage.conversationId;
        if (conversationId) {
          const conversationIndex = state.conversations.findIndex(
            (c) => String(c.id) === String(conversationId),
          );
          if (conversationIndex !== -1) {
            const lastMsg = state.conversations[conversationIndex].lastMessage;
            if (
              lastMsg &&
              (String(lastMsg.id) === String(id) ||
                String((lastMsg as any).tempId) === String(tempId) ||
                String((lastMsg as any).id) === String(tempId))
            ) {
              // Update the conversation's last message with the normalized status
              state.conversations[conversationIndex].lastMessage = normalizedMessage;

              // If status updated to read, clear unread count
              if (normalizedMessage.status === 'read' || normalizedMessage.isRead) {
                state.conversations[conversationIndex].unreadCount = 0;
              }
            }
          }
        }

        // Also update currentConversation if it matches
        if (state.currentConversation && String(state.currentConversation.id) === String(conversationId)) {
          const lastMsg = state.currentConversation.lastMessage;
          if (
            lastMsg &&
            (String(lastMsg.id) === String(id) ||
              String((lastMsg as any).tempId) === String(tempId) ||
              String((lastMsg as any).id) === String(tempId))
          ) {
            state.currentConversation.lastMessage = normalizedMessage;
          }
        }
      }
    },
    // Update multiple messages at once (useful for mark as read)
    updateMessagesStatus: (
      state,
      action: PayloadAction<{
        ids: (string | number)[];
        updates: Partial<Message> | any;
      }>,
    ) => {
      const { ids, updates } = action.payload;
      const idStrings = new Set(ids.map((id) => String(id)));

      let conversationIdToUpdate: string | number | undefined;

      state.messages = state.messages.map((m) => {
        if (
          idStrings.has(String(m.id)) ||
          ((m as any).tempId && idStrings.has(String((m as any).tempId)))
        ) {
          if (!conversationIdToUpdate && m.conversationId) {
            conversationIdToUpdate = m.conversationId;
          }
          return { ...m, ...updates };
        }
        return m;
      });

      // If marking as read, aggressively clear the unread count on the conversation
      if (
        (updates.status === "read" || updates.isRead) &&
        conversationIdToUpdate
      ) {
        const conversationIndex = state.conversations.findIndex(
          (c) => String(c.id) === String(conversationIdToUpdate),
        );
        if (conversationIndex !== -1) {
          state.conversations[conversationIndex].unreadCount = 0;
        }
      }
    },
    // General purpose optimistic update for any message field
    updateMessageOptimistically: (
      state,
      action: PayloadAction<{
        id: string | number;
        updates: Partial<Message> | any;
      }>,
    ) => {
      const index = state.messages.findIndex(
        (m) =>
          String(m.id) === String(action.payload.id) ||
          String((m as any).tempId) === String(action.payload.id),
      );
      if (index !== -1) {
        const updatedMessage = {
          ...state.messages[index],
          ...action.payload.updates,
        };
        // Ensure consistency by normalizing after optimistic update
        state.messages[index] = normalizeMessage(updatedMessage);
      }
    },
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Conversations
      .addCase(fetchConversations.pending, (state, action) => {
        const append = !!action.meta.arg?.append;
        if (append) {
          state.isLoadingMoreConversations = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMoreConversations = false;
        const { conversations: incoming, pagination, append } = action.payload;
        state.conversationsPagination = pagination;
        if (append) {
          const existingIds = new Set(
            state.conversations.map((c) => String(c.id)),
          );
          const unique = incoming.filter(
            (c) => !existingIds.has(String(c.id)),
          );
          state.conversations.push(...unique);
        } else {
          state.conversations = incoming;
        }
        state.error = null;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMoreConversations = false;
        state.error = action.payload;
      })

      // Fetch Conversation By ID
      .addCase(fetchConversationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentConversation = action.payload.conversation;
        // Normalize messages to ensure reaction and isIncoming fields are correctly set
        state.messages = (action.payload.messages || []).map((m) =>
          normalizeMessage(m, action.payload.currentUserId),
        );
        state.error = null;
      })
      .addCase(fetchConversationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Messages (Paginated)
      .addCase(fetchMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        let normalizedMessages = action.payload.messages.map((m) =>
          normalizeMessage(m, action.payload.currentUserId),
        );

        // Post-process to extract images and documents from files array
        normalizedMessages = normalizedMessages.map((msg: any) => {
          const processed = { ...msg };

          if (msg.files && msg.files.length > 0) {
            // Only extract if images/documents don't already exist or are empty
            if (!msg.images || msg.images.length === 0) {
              const images = msg.files.filter((f: any) =>
                f.fileType?.startsWith('image/') || f.fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i)
              ).map((f: any) => ({
                name: f.fileName || f.name,
                type: f.fileType || 'image',
                fileUrl: f.fileUrl || f.url,
                fileName: f.fileName || f.name
              }));
              if (images.length > 0) {
                processed.images = images;
              }
            }

            if (!msg.documents || msg.documents.length === 0) {
              const documents = msg.files.filter((f: any) => {
                const isVoice = f.fileType === 'audio/webm' || f.fileUrl?.match(/\.webm$/i) || msg.messageType === 'VOICE' || msg.type === 'voice';
                return !isVoice && !f.fileType?.startsWith('image/') && !f.fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
              }).map((f: any) => ({
                name: f.fileName || f.name,
                type: f.fileType || 'document',
                fileUrl: f.fileUrl || f.url,
                fileName: f.fileName || f.name
              }));
              if (documents.length > 0) {
                processed.documents = documents;
              }
            }
          }

          return processed;
        });

        if (action.payload.reset) {
          state.messages = normalizedMessages;
        } else {
          const existingIds = new Set(state.messages.map((m) => String(m.id)));
          const uniqueIncoming = normalizedMessages.filter(
            (m) => !existingIds.has(String(m.id)),
          );

          // When loading older history (page > 1), prepend so chronological order remains correct.
          if ((action.payload as any).page && (action.payload as any).page > 1) {
            state.messages = [...uniqueIncoming, ...state.messages];
          } else {
            state.messages = [...state.messages, ...uniqueIncoming];
          }
        }
        state.error = null;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Send Message
      .addCase(sendMessage.pending, (state) => {
        state.isSendingMessage = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isSendingMessage = false;
        const newMessage = action.payload;

        // Normalize the message status to ensure consistency
        const normalizedMessage = normalizeMessage(newMessage, (action.payload as any).currentUserId);

        // Add message if not already in the list (to avoid duplicates with optimistic updates)
        const exists = state.messages.some((m) => m.id === normalizedMessage.id);
        if (!exists) {
          state.messages.push(normalizedMessage);
        }

        // Update conversation's last message with normalized status
        const conversationIndex = state.conversations.findIndex(
          (c) => String(c.id) === String(normalizedMessage.conversationId),
        );
        if (conversationIndex !== -1) {
          state.conversations[conversationIndex].lastMessage = normalizedMessage;
          // Ensure the conversation is moved to the top
          const conversation = state.conversations.splice(conversationIndex, 1)[0];
          state.conversations.unshift(conversation);
        }

        // Also update currentConversation if it matches
        if (state.currentConversation && String(state.currentConversation.id) === String(normalizedMessage.conversationId)) {
          state.currentConversation.lastMessage = normalizedMessage;
        }

        state.error = null;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSendingMessage = false;
        state.error = action.payload;
      })

      // Mark Message Read
      .addCase(markMessageRead.fulfilled, (state, action) => {
        if (action.payload && "messageId" in action.payload) {
          const messageId = action.payload.messageId;
          const message = state.messages.find((m) => m.id === messageId);
          if (message) {
            message.isRead = true;
          }
        }
      })

      // Mark Conversation Read
      .addCase(markConversationRead.fulfilled, (state, action) => {
        if (action.payload && "conversationId" in action.payload) {
          const conversationId = action.payload.conversationId;

          // Mark all messages in current conversation as read
          state.messages = state.messages.map((m) =>
            m.conversationId === conversationId ? { ...m, isRead: true } : m,
          );

          // Update conversation unread count
          const conversation = state.conversations.find(
            (c) => c.id === conversationId,
          );
          if (conversation) {
            conversation.unreadCount = 0;
          }
        }
      })

      // Fetch Conversations By Product
      .addCase(fetchConversationsByProduct.pending, (state, action) => {
        const append = !!action.meta.arg?.append;
        if (append) {
          state.isLoadingMoreProductConversations = true;
        } else {
          state.isLoadingProductConversations = true;
        }
        state.error = null;
      })
      .addCase(fetchConversationsByProduct.fulfilled, (state, action) => {
        state.isLoadingProductConversations = false;
        state.isLoadingMoreProductConversations = false;
        const { conversations: incoming, pagination, append } = action.payload;
        state.productConversationsPagination = pagination;
        if (append) {
          const existingIds = new Set(
            state.productConversations.map((c) => String(c.id)),
          );
          const unique = incoming.filter(
            (c) => !existingIds.has(String(c.id)),
          );
          state.productConversations.push(...unique);
        } else {
          state.productConversations = incoming;
        }
        state.error = null;
      })
      .addCase(fetchConversationsByProduct.rejected, (state, action) => {
        state.isLoadingProductConversations = false;
        state.isLoadingMoreProductConversations = false;
        state.error = action.payload;
      })

      // Create Conversation
      .addCase(createConversation.pending, (state) => {
        state.error = null;
      })
      .addCase(createConversation.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(createConversation.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentConversation,
  clearMessages,
  clearProductConversations,
  addMessageOptimistically,
  updateMessageStatus,
  updateMessagesStatus,
  updateMessageOptimistically,
  setMessages: setChatMessages,
} = chatSlice.actions;

export default chatSlice.reducer;
